import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getApiUrl } from '../config/api';
import RatingBadge from './RatingBadge';
import RatingDisplay from './RatingDisplay';
import TokenUsageModal from './TokenUsageModal';
import { useToast } from '../context/ToastContext';
import { ensureAuthenticated } from '../services/authGuardService';
import { getApiErrorMessage, readJsonSafe } from '../services/apiResponseService';
import { getStoredUser } from '../services/subscriptionService';
import styles from '../styles/modules/SearchPages.module.css';
import jobStyles from '../styles/modules/SearchJob.module.css';
import headerStyles from '../styles/modules/Header.module.css';

interface Employer {
    _id: string;
    username: string;
    fullName: string;
    businessName?: string;
    email: string;
    phone?: string;
    role: string;
    profilePicture?: string;
}

interface JobRequest {
    _id: string;
    userId: string;
    jobRole: string;
    date?: string;
    startDate?: string;
    endDate?: string;
    startTime?: string;
    endTime?: string;
    place: string;
    payForTransport?: boolean;
    status: string;
    description?: string;
    payRate?: string;
    facilityName?: string;
    facilityLogo?: string;
    numberOfStaff?: number;
    employer: Employer;
    createdAt: string;
    updatedAt: string;
}

const SearchJob: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { showToast } = useToast();
    const [filters, setFilters] = useState({
        jobRole: '',
        date: '',
        place: '',
        payForTransport: '',
        status: 'open'
    });
    const [results, setResults] = useState<JobRequest[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
    const [creatingEngagement, setCreatingEngagement] = useState<string | null>(null);
    const [selectedEmployerForReviews, setSelectedEmployerForReviews] = useState<string | null>(null);
    const [showTokenModal, setShowTokenModal] = useState(false);

    const jobTypes = [
        'Barman',
        'Bartender',
        'Waiter',
        'Waitress',
        'Chef',
        'Sous Chef',
        'Cook',
        'Dishwasher',
        'Host/Hostess',
        'Sommelier',
        'Busser',
        'Kitchen Porter',
        'Line Cook',
        'Pastry Chef'
    ];

    // Load all open jobs on component mount
    useEffect(() => {
        const loadJobs = async () => {
            setLoading(true);
            setError('');
            
            try {
                const params = new URLSearchParams();
                if (filters.jobRole) params.append('jobRole', filters.jobRole);
                if (filters.date) params.append('date', filters.date);
                if (filters.place.trim()) params.append('place', filters.place.trim());
                if (filters.payForTransport) params.append('payForTransport', filters.payForTransport);
                if (filters.status) params.append('status', filters.status);

                const queryString = params.toString();
                const url = getApiUrl(`job-requests/search${queryString ? `?${queryString}` : ''}`);

                const response = await fetch(url);

                if (!response.ok) {
                    throw new Error(`Search failed: ${response.statusText}`);
                }

                const data = await response.json();
                setResults(data);
            } catch (error) {
                console.error('Error loading jobs:', error);
                setError(error instanceof Error ? error.message : 'Failed to load jobs');
            } finally {
                setLoading(false);
            }
        };

        loadJobs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const searchJobs = async () => {
        const currentUser = getStoredUser();
        
        // Check if user has active quick search window
        if (!currentUser?.activeQuickSearchExpiry) {
            // Show token modal to activate quick search
            setShowTokenModal(true);
            return;
        }

        // Check if the expiry has passed
        const expiryDate = new Date(currentUser.activeQuickSearchExpiry);
        if (expiryDate < new Date()) {
            // Expired, show token modal to reactivate
            setShowTokenModal(true);
            return;
        }

        setLoading(true);
        setError('');
        
        try {
            const params = new URLSearchParams();
            if (filters.jobRole) params.append('jobRole', filters.jobRole);
            if (filters.date) params.append('date', filters.date);
            if (filters.place.trim()) params.append('place', filters.place.trim());
            if (filters.payForTransport) params.append('payForTransport', filters.payForTransport);
            if (filters.status) params.append('status', filters.status);

            const queryString = params.toString();
            const url = getApiUrl(`job-requests/search${queryString ? `?${queryString}` : ''}`);
            
            console.log('Searching with URL:', url);

            const response = await fetch(url);

            if (!response.ok) {
                throw new Error(`Search failed: ${response.statusText}`);
            }

            const data = await response.json();
            setResults(data);
            console.log('Search results:', data);
        } catch (error) {
            console.error('Error searching jobs:', error);
            setError(error instanceof Error ? error.message : 'Failed to search jobs');
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        searchJobs();
    };

    const handleClearFilters = () => {
        setFilters({
            jobRole: '',
            date: '',
            place: '',
            payForTransport: '',
            status: 'open'
        });
        setTimeout(() => searchJobs(), 0);
    };

    const handleTokenUsed = () => {
        setShowTokenModal(false);
        showToast({ message: t('tokenUsage.quickSearchActivated'), type: 'success' });
    };

    const toggleCardExpanded = (id: string) => {
        setExpandedCards(prev => {
            const newSet = new Set(prev);
            if (newSet.has(id)) {
                newSet.delete(id);
            } else {
                newSet.add(id);
            }
            return newSet;
        });
    };

    const handleCreateEngagement = async (job: JobRequest) => {
        const auth = ensureAuthenticated();

        if (!auth.allowed) {
            showToast({ message: t('searchJob.engagementLoginRequired'), type: 'error' });
            navigate(auth.redirectTo);
            return;
        }

        const token = auth.token;
        const currentUser = auth.user;
        setCreatingEngagement(job._id);

        try {
            const engagementData: any = {
                bartenderId: currentUser.id,
                employerId: job.employer._id,
                jobRequestId: job._id,
                jobRole: job.jobRole,
                place: job.place,
                facilityName: job.facilityName || '',
                payRate: job.payRate || '',
                description: job.description || '',
                notes: ''
            };

            // Add date/time fields based on job type
            if (job.date) {
                engagementData.date = job.date;
                if (job.startTime) engagementData.startTime = job.startTime;
                if (job.endTime) engagementData.endTime = job.endTime;
            } else if (job.startDate && job.endDate) {
                engagementData.startDate = job.startDate;
                engagementData.endDate = job.endDate;
            }

            const response = await fetch(getApiUrl('engagements'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(engagementData)
            });

            if (!response.ok) {
                const errorData = await readJsonSafe<any>(response);
                throw new Error(getApiErrorMessage(response, t('searchJob.engagementCreateFailed') || 'Failed to create engagement', errorData));
            }

            const result = await response.json();
            showToast({ message: t('searchJob.engagementCreateSuccess'), type: 'success' });
            console.log('Engagement created:', result);
        } catch (error) {
            console.error('Error creating engagement:', error);
            showToast({ message: error instanceof Error ? error.message : t('searchJob.engagementCreateFailed'), type: 'error' });
        } finally {
            setCreatingEngagement(null);
        }
    };

    const handleBackClick = () => {
        navigate('/dashboard');
    };

    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        const locale = t('common.locale') || 'en-US';
        return date.toLocaleDateString(locale, { 
            weekday: 'short',
            year: 'numeric', 
            month: 'short', 
            day: 'numeric' 
        });
    };

    const formatTime = (time: string): string => {
        const [hours, minutes] = time.split(':');
        const hour = parseInt(hours);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const hour12 = hour % 12 || 12;
        return `${hour12}:${minutes} ${ampm}`;
    };

    const getEmployerContactHref = (employer: Employer): string => {
        const phone = employer.phone?.trim();
        return phone ? `tel:${phone}` : `mailto:${employer.email}`;
    };

    const getPayRateText = (job: JobRequest): string => {
        return job.payRate?.trim() || t('profile.notProvided');
    };

    const getTransportText = (job: JobRequest): string => {
        if (job.payForTransport === true) {
            return t('searchJob.transportIncluded');
        }

        if (job.payForTransport === false) {
            return t('searchJob.noTransportProvided');
        }

        return t('profile.notProvided');
    };

    const handleOpenLocation = (location: string): void => {
        const encodedLocation = encodeURIComponent(location.trim());

        if (!encodedLocation) {
            return;
        }

        const userAgent = navigator.userAgent || navigator.vendor;
        const isIosDevice = /iPad|iPhone|iPod/i.test(userAgent);
        const isAndroidDevice = /Android/i.test(userAgent);

        if (isAndroidDevice) {
            window.location.href = `geo:0,0?q=${encodedLocation}`;
            return;
        }

        if (isIosDevice) {
            window.open(`http://maps.apple.com/?q=${encodedLocation}`, '_blank', 'noopener,noreferrer');
            return;
        }

        window.open(`https://www.google.com/maps/search/?api=1&query=${encodedLocation}`, '_blank', 'noopener,noreferrer');
    };

    return (
        <div className={styles.pageContainer}>
            <header className={headerStyles.header}>
                <button
                    type="button"
                    onClick={handleBackClick}
                    className={`${headerStyles.backButton} ${headerStyles.backButtonAbsolute}`}
                    aria-label={t('common.back')}
                    title={t('common.back')}
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 12H5M12 19l-7-7 7-7"/>
                    </svg>
                </button>
                <h1 className={headerStyles.titleDark}>{t('searchJob.title')}</h1>
                <div></div>
            </header>

            {/* Filters Form */}
            <form onSubmit={handleSearch} className={styles.filtersForm}>
                <div className={styles.filtersGrid}>
                    <div className={styles.filterGroup}>
                        <label htmlFor="jobRole" className={styles.filterLabel}>{t('searchJob.jobType')}</label>
                                    <select
                                        id="jobRole"
                        name="jobRole"
                        value={filters.jobRole}
                        onChange={handleFilterChange}
                        className={styles.filterInput}
                    >
                        <option value="">{t('searchJob.selectJobType')}</option>
                        {jobTypes.map((job) => (
                            <option key={job} value={job}>
                                {t(`roles.${job.toLowerCase().replace(/\s+/g, '').replace('/', '')}`)}
                            </option>
                        ))}
                    </select>
                </div>

                <div className={styles.filterGroup}>
                    <label htmlFor="date" className={styles.filterLabel}>{t('searchJob.date')}</label>
                    <input
                        type="date"
                        id="date"
                        name="date"
                        value={filters.date}
                        onChange={handleFilterChange}
                        className={styles.filterInput}
                        min={new Date().toISOString().split('T')[0]}
                    />
                </div>

                <div className={styles.filterGroup}>
                    <label htmlFor="place" className={styles.filterLabel}>{t('searchJob.location')}</label>
                    <input
                        type="text"
                        id="place"
                        name="place"
                        value={filters.place}
                        onChange={handleFilterChange}
                        className={styles.filterInput}
                        placeholder={t('searchJob.locationPlaceholder')}
                    />
                </div>

                <div className={styles.filterGroup}>
                    <label htmlFor="payForTransport" className={styles.filterLabel}>{t('searchJob.transport')}</label>
                    <select
                        id="payForTransport"
                        name="payForTransport"
                        value={filters.payForTransport}
                        onChange={handleFilterChange}
                        className={styles.filterInput}
                    >
                        <option value="">{t('searchJob.any')}</option>
                        <option value="true">{t('searchJob.transportProvided')}</option>
                        <option value="false">{t('searchJob.noTransportProvided')}</option>
                    </select>
                </div>

                <div className={styles.filterGroup}>
                    <label htmlFor="status" className={styles.filterLabel}>{t('searchJob.statusFilter')}</label>
                    <select
                        id="status"
                        name="status"
                        value={filters.status}
                        onChange={handleFilterChange}
                        className={styles.filterInput}
                    >
                        <option value="open">{t('jobOffers.open')}</option>
                        <option value="filled">{t('jobOffers.filled')}</option>
                        <option value="cancelled">{t('jobOffers.cancelled')}</option>
                    </select>
                </div>
            </div>

            <div className={styles.filtersActions}>
                <button type="submit" className={styles.searchButton} disabled={loading}>
                    {loading ? t('searchJob.searching') : t('searchJob.search')}
                </button>
                <button type="button" onClick={handleClearFilters} className={styles.clearButton}>
                    {t('searchJob.clearFilters')}
                </button>
            </div>
        </form>

            {/* Error Message */}
            {error && (
                <div className={styles.error}>
                    {error}
                </div>
            )}

            {/* Results */}
            <div className={styles.resultsSection}>
                        <h2 className={styles.resultsHeader}>
                            {loading ? t('searchJob.searching') : `${t('searchJob.found')} ${results.length} ${results.length !== 1 ? t('searchJob.jobs') : t('searchJob.job')}`}
                        </h2>

                        {loading ? (
                            <div className={styles.loading}>{t('common.loading')}</div>
                        ) : results.length === 0 ? (
                            <div className={styles.noResults}>
                                <p>{t('searchJob.noResults')}</p>
                                <p>{t('searchJob.tryAdjusting')}</p>
                            </div>
                        ) : (
                            <div className={styles.resultsGrid}>
                                {results.map((job) => {
                                    const isExpanded = expandedCards.has(job._id);
                                    const hasDescription = Boolean(job.description?.trim());
                                    
                                    return (
                                        <div key={job._id} className={jobStyles.jobCard}>
                                            <div className={jobStyles.cardHeader}>
                                                <div className={jobStyles.jobMainInfo}>
                                                    <h3 className={jobStyles.jobRole}>
                                                        {t(`roles.${job.jobRole.toLowerCase().replace(/\s+/g, '').replace('/', '').replace('&', '')}`)}
                                                        {job.numberOfStaff && job.numberOfStaff > 1 && (
                                                            <span className={jobStyles.staffCount}> ({job.numberOfStaff})</span>
                                                        )}
                                                    </h3>
                                                    <div className={`${jobStyles.statusBadge} ${job.status === 'open' ? jobStyles.statusOpen : job.status === 'filled' ? jobStyles.statusFilled : jobStyles.statusCancelled}`}>
                                                        {t(`searchJob.status.${job.status}`)}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className={jobStyles.cardBody}>
                                                {/* Facility Image and Name */}
                                                {(job.employer.profilePicture || job.facilityLogo || job.facilityName) && (
                                                    <div className={jobStyles.facilityHeader}>
                                                        {job.employer.profilePicture ? (
                                                            <img 
                                                                src={job.employer.profilePicture} 
                                                                alt={job.employer.fullName}
                                                                className={jobStyles.facilityHeaderImage}
                                                            />
                                                        ) : job.facilityLogo ? (
                                                            <img 
                                                                src={job.facilityLogo} 
                                                                alt={job.facilityName}
                                                                className={jobStyles.facilityHeaderImage}
                                                            />
                                                        ) : null}
                                                        {job.facilityName && (
                                                            <h4 className={jobStyles.facilityHeaderName}>{job.facilityName}</h4>
                                                        )}
                                                    </div>
                                                )}

                                                <div className={jobStyles.jobInfo}>
                                                    <div className={jobStyles.infoRow}>
                                                        <button
                                                            type="button"
                                                            className={`${jobStyles.infoIconButton} ${jobStyles.locationPinButton}`}
                                                            onClick={() => handleOpenLocation(job.place)}
                                                            aria-label={`${t('searchJob.openInMaps')}: ${job.place}`}
                                                            title={`${t('searchJob.openInMaps')}: ${job.place}`}
                                                        >
                                                            <span className={jobStyles.infoIcon}>📍</span>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className={`${jobStyles.infoText} ${jobStyles.locationTextButton}`}
                                                            onClick={() => handleOpenLocation(job.place)}
                                                            title={`${t('searchJob.openInMaps')}: ${job.place}`}
                                                        >
                                                            {job.place}
                                                        </button>
                                                    </div>
                                                    {job.date ? (
                                                        <>
                                                            <div className={jobStyles.infoRow}>
                                                                <span className={jobStyles.infoIcon}>📅</span>
                                                                <span className={jobStyles.infoText}>{formatDate(job.date)}</span>
                                                            </div>
                                                            {job.startTime && (
                                                                <div className={jobStyles.infoRow}>
                                                                    <span className={jobStyles.infoIcon}>🕒</span>
                                                                    <span className={jobStyles.infoText}>
                                                                        {formatTime(job.startTime)}
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </>
                                                    ) : job.startDate && job.endDate ? (
                                                        <div className={jobStyles.infoRow}>
                                                            <span className={jobStyles.infoIcon}>📅</span>
                                                            <span className={jobStyles.infoText}>
                                                                {formatDate(job.startDate)} - {formatDate(job.endDate)}
                                                            </span>
                                                        </div>
                                                    ) : null}
                                                    <div className={`${jobStyles.infoRow} ${jobStyles.payRate}`}>
                                                        <span className={jobStyles.infoIcon}>💰</span>
                                                        <span className={jobStyles.infoText}>
                                                            <span className={jobStyles.infoLabel}>{t('searchJob.payment')}:</span>{' '}
                                                            {getPayRateText(job)}
                                                        </span>
                                                    </div>
                                                    <div className={`${jobStyles.infoRow} ${job.payForTransport === true ? jobStyles.transportProvided : ''}`}>
                                                        <span className={jobStyles.infoIcon}>🚗</span>
                                                        <span className={jobStyles.infoText}>
                                                            <span className={jobStyles.infoLabel}>{t('searchJob.transport')}:</span>{' '}
                                                            {getTransportText(job)}
                                                        </span>
                                                    </div>
                                                </div>

                                                {/* Employer Info */}
                                                <div className={jobStyles.employerSection}>
                                                    <div className={jobStyles.infoRow}>
                                                        <span className={jobStyles.infoIcon}>👤</span>
                                                        <div className={`${jobStyles.infoText} ${jobStyles.employerInfo}`}>
                                                            <div>
                                                                <span className={jobStyles.employerName}>{job.employer.businessName || job.employer.fullName}</span>
                                                                {!job.employer.businessName && <span className={jobStyles.employerUsername}>@{job.employer.username}</span>}
                                                            </div>
                                                            <button
                                                                type="button"
                                                                className={`${jobStyles.ratingContainer} ${jobStyles.ratingButton}`}
                                                                onClick={() => setSelectedEmployerForReviews(job.employer._id)}
                                                                aria-label={`${job.employer.fullName} ${t('searchJob.employerReviews')}`}
                                                                title={`${job.employer.fullName} ${t('searchJob.employerReviews')}`}
                                                            >
                                                                <RatingBadge userId={job.employer._id} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Description Section */}
                                                <div className={jobStyles.descriptionSection}>
                                                    <button
                                                        type="button"
                                                        className={`${jobStyles.toggleDescriptionBtn} ${!hasDescription ? jobStyles.toggleDescriptionBtnDisabled : ''}`}
                                                        onClick={() => toggleCardExpanded(job._id)}
                                                        disabled={!hasDescription}
                                                    >
                                                        {isExpanded && hasDescription ? '▼' : '▶'} {t('searchJob.description')}
                                                    </button>

                                                    {hasDescription && isExpanded && (
                                                        <div className={jobStyles.descriptionContent}>
                                                            {job.description}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className={jobStyles.cardFooter}>
                                                <a href={getEmployerContactHref(job.employer)} className={jobStyles.contactButton}>
                                                    {t('searchJob.contactEmployer')}
                                                </a>
                                                <button 
                                                    onClick={() => handleCreateEngagement(job)}
                                                    className={jobStyles.engagementButton}
                                                    disabled={creatingEngagement === job._id}
                                                >
                                                    {creatingEngagement === job._id ? '...' : t('searchJob.createEngagement')}
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
            </div>

            {/* Reviews Modal */}
            {selectedEmployerForReviews && (
                <div 
                    className={jobStyles.modalOverlay}
                    onClick={() => setSelectedEmployerForReviews(null)}
                >
                    <div 
                        className={jobStyles.reviewsModal}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className={jobStyles.modalHeader}>
                            <h2>{t('searchJob.employerReviews')}</h2>
                            <button 
                                className={jobStyles.closeModal}
                                onClick={() => setSelectedEmployerForReviews(null)}
                                aria-label="Close"
                            >
                                ×
                            </button>
                        </div>
                        <div className={jobStyles.modalBody}>
                            <RatingDisplay userId={selectedEmployerForReviews} showSummary={false} autoShowReviews={true} hideToggleButton={true} />
                        </div>
                    </div>
                </div>
            )}

            {/* Token Usage Modal */}
            <TokenUsageModal 
                type="quickSearch"
                isOpen={showTokenModal}
                onClose={() => setShowTokenModal(false)}
                onTokenUsed={handleTokenUsed}
            />
        </div>
    );
};

export default SearchJob;
